import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useAppConfigContext } from '@/context/AppConfig';
import { request } from '@/utils/index';

export interface CustomWindow extends Window {
  grecaptcha?: {
    render: (id: string) => string;
    execute: (id?: string) => void;
    reset: (id?: string) => void;
  };
  hcaptcha?: {
    render: (id: string) => string;
    execute: (id?: string) => void;
    reset: (id?: string) => void;
  };
  turnstile?: {
    render: (id: string) => string;
    execute: (id?: string) => void;
    reset: (id?: string) => void;
  };
  reportVelocity?: {
    render: (id: string) => string;
    execute: (id?: string) => void;
    reset: (id?: string) => void;
  };
}

interface ConfigResponse {
  session?: {
    hcap?: boolean;
    recap?: boolean;
    turnstile?: boolean;
    reportVelocity?: boolean;
  };
}

interface CaptchaConfig {
  customUrl?: string;
  customConfigResponse?: ConfigResponse;
  hideChallenge?: boolean;
}

const turnstileUrl = 'https://challenges.cloudflare.com/turnstile/v0/api.js'; // render on load
const hcaptchaUrl = 'https://js.hcaptcha.com/1/api.js?render=explicit';
const recaptchaUrl = 'https://www.google.com/recaptcha/api.js?render=explicit';
const configUrl = '/api/v5/session/new.json';
const hasDevProxyToken =
  window.env?.RECAPTCHA_DEV_PROXY_TOKEN &&
  window.env?.RECAPTCHA_DEV_PROXY_TOKEN !== '%VITE_RECAPTCHA_DEV_PROXY_TOKEN%';

const staticCaptchaConfig: Record<string, Record<string, string>> = {
  turnstile: {
    url: turnstileUrl,
    captcha: 'cf-turnstile',
    key: window.env?.TURNSTILE_SITE_KEY,
  },
  hcaptcha: {
    url: hcaptchaUrl,
    captcha: 'hcaptcha',
    key: window.env?.HCAPTCHA_SITE_KEY,
  },
  recaptcha: {
    url: recaptchaUrl,
    captcha: 'recaptcha',
    key: window.env?.RECAPTCHA_SITE_KEY,
  },
  reportVelocity: {
    url: hcaptchaUrl,
    captcha: 'report-velocity-captcha',
    key: window.env?.HCAPTCHA_REPORT_VELOCITY_TOKEN,
  },
};

const fetchCaptchaConfig = async (customCaptchaConfig?: CaptchaConfig) => {
  if (customCaptchaConfig?.customConfigResponse) {
    return customCaptchaConfig.customConfigResponse;
  }
  const url = customCaptchaConfig?.customUrl || configUrl;
  const options = { method: 'GET' };

  return await request(url, options);
};

const useFetchCaptchaConfig = (customCaptchaConfig?: CaptchaConfig) => {
  const captchaConfig = useQuery({
    queryKey: ['fetchCaptchaConfig'],
    queryFn: () => fetchCaptchaConfig(customCaptchaConfig),
  });
  if (captchaConfig?.data?.session?.turnstile) {
    return staticCaptchaConfig.turnstile;
  }
  if (captchaConfig?.data?.session?.hcap) {
    return staticCaptchaConfig.hcaptcha;
  }
  if (captchaConfig?.data?.session?.recap) {
    return staticCaptchaConfig.recaptcha;
  }
  if (captchaConfig?.data?.session?.reportVelocity) {
    return staticCaptchaConfig.reportVelocity;
  }
  return { url: '', captcha: '' };
};

export const useCaptcha = (customCaptchaConfig?: CaptchaConfig) => {
  const [siteKey, setSiteKey] = useState('');
  const [loadStatus, setLoadStatus] = useState('');
  const [url, setUrl] = useState('');
  const [captcha, setCaptcha] = useState('');
  const captchaConfig = useFetchCaptchaConfig(customCaptchaConfig);
  const hideChallenge = customCaptchaConfig?.hideChallenge || false;
  const { logError } = useAppConfigContext();

  useEffect(() => {
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.onload = () => {
      setLoadStatus('success');
    };
    script.onerror = () => {
      setLoadStatus('failure');
    };
    document.body.appendChild(script);
    script.setAttribute('data-testid', 'hcaptcha-script');
    return () => {
      document.body.removeChild(script);
    };
  }, [url]);

  useEffect(() => {
    if (captchaConfig.url) {
      setUrl(captchaConfig.url);
    }
    if (captchaConfig.captcha) {
      setCaptcha(captchaConfig.captcha);
    }
    if (captchaConfig.key) {
      setSiteKey(captchaConfig.key);
    }
  }, [captchaConfig]);

  const renderCaptcha = (captchaId: string) => {
    if (loadStatus === 'success' && !hasDevProxyToken) {
      try {
        if ((window as CustomWindow)?.turnstile) {
          // turnstile renders and executes on load
        } else if ((window as CustomWindow)?.hcaptcha) {
          const hcaptchaId = (window as CustomWindow).hcaptcha?.render(
            captchaId
          );
          if (!hideChallenge) {
            (window as CustomWindow)?.hcaptcha?.execute(hcaptchaId);
          }
        } else if ((window as CustomWindow)?.grecaptcha?.render) {
          const recaptchaId = (window as CustomWindow).grecaptcha?.render(
            captchaId
          );
          (window as CustomWindow)?.grecaptcha?.execute(recaptchaId);
        }
      } catch (e: unknown) {
        if (e instanceof Error) {
          logError('captcha', e);
        }
      }
    }
  };

  return [captcha, loadStatus === 'success', siteKey, renderCaptcha] as [
    string,
    boolean,
    string,
    (id: string) => void
  ];
};
